import dynamic from 'next/dynamic';
import React, { createContext, useState, useContext, Suspense } from 'react';

import GetHelpModal, { GetHelpModalProps } from '@Components/get-help-modal';
import { SelectClinicModalProps } from '@Components/select-clinic-modal';
import { IAddress } from '@Services/Patient/patient-payload';
import { FileType } from '@Utils/Helpers/PatientFiles/helper';

const UploadDocument = dynamic(() => import('@Components/records/upload-document'), {
  ssr: false,
});
const SelectClinicModal = dynamic(() => import('@Components/select-clinic-modal'), {
  ssr: false,
});
const TakeAddressFlow = dynamic(() => import('@Components/address/flow'), {
  ssr: false,
});

export enum DashboardModalType {
  ADD_ADDRESS = 'ADD_ADDRESS',
  UPLOAD_DOCUMENT = 'UPLOAD_DOCUMENT',
  SELECT_CLINIC = 'SELECT_CLINIC',
  GET_HELP = 'GET_HELP',
}

interface ModalState {
  type: DashboardModalType;
  props: AddAddressModalProps | UploadDocumentModalProps | Omit<SelectClinicModalProps, 'onClose'> | GetHelpModalProps;
}

interface AddAddressModalProps {
  preSelectedAddress?: IAddress;
  onConfirm: (address: IAddress) => void;
}

interface UploadDocumentModalProps {
  attachmentType: FileType;
}

interface DashboardModalContextProps {
  showModal: (modalState: ModalState) => void;
  hideModal: () => void;
  modalState: ModalState | false;
}

const DashboardModalContext = createContext<DashboardModalContextProps>(undefined);

export const useModal = (): DashboardModalContextProps => {
  const context = useContext(DashboardModalContext);
  if (!context) {
    throw new Error('useModal must be used within DashboardModalProvider');
  }
  return context;
};

interface DashboardModalProviderProps {
  children: React.ReactNode;
}

export const DashboardModalProvider: React.FC<DashboardModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState | false>(false);

  const showModal = (newModalState: ModalState) => {
    setModalState(newModalState);
  };

  const hideModal = () => {
    setModalState(false);
  };

  const modalToRender = () => {
    if (!modalState) return;

    switch (modalState.type) {
      case DashboardModalType.ADD_ADDRESS:
        return (
          <TakeAddressFlow
            onConfirm={(modalState.props as AddAddressModalProps).onConfirm}
            onClose={hideModal}
            preSelectedAddress={(modalState.props as AddAddressModalProps).preSelectedAddress}
          />
        );
      case DashboardModalType.UPLOAD_DOCUMENT:
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <UploadDocument
              closeModal={hideModal}
              attachmenType={(modalState.props as UploadDocumentModalProps).attachmentType}
            />
          </Suspense>
        );
      case DashboardModalType.SELECT_CLINIC:
        return (
          <SelectClinicModal
            onClose={hideModal}
            onClinicChange={(modalState.props as SelectClinicModalProps).onClinicChange}
            currentClinic={(modalState.props as SelectClinicModalProps).currentClinic}
            userCity={(modalState.props as SelectClinicModalProps).userCity}
          />
        );
      case DashboardModalType.GET_HELP:
        const { onClose: userOnClose, ...modalProps } = modalState.props as GetHelpModalProps;
        const handleClose = () => {
          hideModal();
          if (typeof userOnClose === 'function') {
            userOnClose();
          }
        };

        return <GetHelpModal {...modalProps} onClose={handleClose} />;
    }
  };

  return (
    <DashboardModalContext.Provider value={{ showModal, hideModal, modalState }}>
      {children}
      {modalState && modalToRender()}
    </DashboardModalContext.Provider>
  );
};
